import { 
  collection, 
  getDocs, 
  getDoc, 
  doc, 
  setDoc, 
  updateDoc,
  query, 
  where, 
  orderBy, 
  limit,
  serverTimestamp,
  deleteDoc
} from 'firebase/firestore';
import { firestore } from '../firebase/config';

/**
 * NotificationService - Handles user notifications and updates
 */
export class NotificationService {
  /**
   * Create a new notification/update
   * @param {Object} notificationData - Notification data
   * @returns {Object} { success: boolean, message: string, notificationId: string }
   */
  static async createNotification(notificationData) {
    const { 
      userId, 
      type, 
      title, 
      message, 
      relatedId = null,
      metadata = {} 
    } = notificationData;

    if (!userId || !type || !title || !message) {
      throw new Error('Missing required notification data');
    }

    try {
      const notificationId = doc(collection(firestore, 'notifications')).id;
      const notificationRef = doc(firestore, 'notifications', notificationId);
      
      const notificationDoc = {
        id: notificationId,
        userId,
        type, // 'application', 'job', 'message', 'profile'
        title,
        message,
        relatedId, // jobId, applicationId, etc.
        metadata,
        read: false,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };
      
      await setDoc(notificationRef, notificationDoc);
      
      return {
        success: true,
        message: 'Notification created successfully',
        notificationId
      };
      
    } catch (error) {
      console.error('❌ NotificationService: Error creating notification:', error);
      throw new Error(`Failed to create notification: ${error.message}`);
    }
  }

  /**
   * Get all notifications for a user
   * @param {string} userId - User ID
   * @param {Object} options - Query options
   * @returns {Array} Array of notifications
   */
  static async getUserNotifications(userId, options = {}) {
    if (!userId) {
      return [];
    }

    const { 
      type = null, 
      readStatus = null, 
      limit: queryLimit = 50 
    } = options;

    try {
      const notificationsRef = collection(firestore, 'notifications');
      
      // Build base query
      let notificationsQuery = query(
        notificationsRef,
        where('userId', '==', userId),
        orderBy('createdAt', 'desc'),
        limit(queryLimit)
      );
      
      // Add filters if provided
      if (type) {
        notificationsQuery = query(
          notificationsRef,
          where('userId', '==', userId),
          where('type', '==', type),
          orderBy('createdAt', 'desc'),
          limit(queryLimit)
        );
      }
      
      if (readStatus !== null) {
        const baseQuery = type ? 
          query(
            notificationsRef,
            where('userId', '==', userId),
            where('type', '==', type),
            where('read', '==', readStatus),
            orderBy('createdAt', 'desc'),
            limit(queryLimit)
          ) :
          query(
            notificationsRef,
            where('userId', '==', userId),
            where('read', '==', readStatus),
            orderBy('createdAt', 'desc'),
            limit(queryLimit)
          );
        notificationsQuery = baseQuery;
      }
      
      const snapshot = await getDocs(notificationsQuery);
      
      const notifications = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          // Convert Firestore timestamp to readable format
          time: data.createdAt ? this.formatTimeAgo(data.createdAt.toDate()) : 'Just now'
        };
      });
      
      return notifications;
      
    } catch (error) {
      console.error('❌ NotificationService: Error fetching notifications:', error);
      throw new Error(`Failed to fetch notifications: ${error.message}`);
    }
  }

  /**
   * Mark a notification as read
   * @param {string} notificationId - Notification ID
   * @returns {Object} { success: boolean, message: string }
   */
  static async markAsRead(notificationId) {
    if (!notificationId) {
      throw new Error('Notification ID is required');
    }

    try {
      const notificationRef = doc(firestore, 'notifications', notificationId);
      
      await updateDoc(notificationRef, {
        read: true,
        updatedAt: serverTimestamp()
      });
      
      return {
        success: true,
        message: 'Notification marked as read'
      };
      
    } catch (error) {
      console.error('❌ NotificationService: Error marking notification as read:', error);
      throw new Error(`Failed to mark notification as read: ${error.message}`);
    }
  }

  /**
   * Mark all notifications as read for a user
   * @param {string} userId - User ID
   * @returns {Object} { success: boolean, message: string, updatedCount: number }
   */
  static async markAllAsRead(userId) {
    if (!userId) {
      throw new Error('User ID is required');
    }

    try {
      // Get all unread notifications
      const unreadNotifications = await this.getUserNotifications(userId, { 
        readStatus: false, 
        limit: 100 
      });
      
      // Update each notification
      const updatePromises = unreadNotifications.map(notification => 
        updateDoc(doc(firestore, 'notifications', notification.id), {
          read: true,
          updatedAt: serverTimestamp()
        })
      );
      
      await Promise.all(updatePromises);
      
      return {
        success: true,
        message: `Marked ${unreadNotifications.length} notifications as read`,
        updatedCount: unreadNotifications.length
      };
      
    } catch (error) {
      console.error('❌ NotificationService: Error marking all notifications as read:', error);
      throw new Error(`Failed to mark all notifications as read: ${error.message}`);
    }
  }

  /**
   * Delete a notification
   * @param {string} notificationId - Notification ID
   * @returns {Object} { success: boolean, message: string }
   */
  static async deleteNotification(notificationId) {
    if (!notificationId) {
      throw new Error('Notification ID is required');
    }

    try {
      const notificationRef = doc(firestore, 'notifications', notificationId);
      await deleteDoc(notificationRef);
      
      return {
        success: true,
        message: 'Notification deleted successfully'
      };
      
    } catch (error) {
      console.error('❌ NotificationService: Error deleting notification:', error);
      throw new Error(`Failed to delete notification: ${error.message}`);
    }
  }

  /**
   * Get user notification preferences
   * @param {string} userId - User ID
   * @returns {Object} User notification preferences
   */
  static async getNotificationPreferences(userId) {
    if (!userId) {
      return this.getDefaultPreferences();
    }

    try {
      const preferencesRef = doc(firestore, 'notificationPreferences', userId);
      const preferencesDoc = await getDoc(preferencesRef);
      
      if (preferencesDoc.exists()) {
        return {
          ...this.getDefaultPreferences(),
          ...preferencesDoc.data()
        };
      }
      
      return this.getDefaultPreferences();
      
    } catch (error) {
      console.error('❌ NotificationService: Error fetching preferences:', error);
      return this.getDefaultPreferences();
    }
  }

  /**
   * Update user notification preferences
   * @param {string} userId - User ID
   * @param {Object} preferences - Preferences object
   * @returns {Object} { success: boolean, message: string }
   */
  static async updateNotificationPreferences(userId, preferences) {
    if (!userId) {
      throw new Error('User ID is required');
    }

    try {
      const preferencesRef = doc(firestore, 'notificationPreferences', userId);
      
      const updatedPreferences = {
        ...preferences,
        userId,
        updatedAt: serverTimestamp()
      };
      
      await setDoc(preferencesRef, updatedPreferences, { merge: true });
      
      return {
        success: true,
        message: 'Notification preferences updated successfully'
      };
      
    } catch (error) {
      console.error('❌ NotificationService: Error updating preferences:', error);
      throw new Error(`Failed to update notification preferences: ${error.message}`);
    }
  }

  /**
   * Get default notification preferences
   * @returns {Object} Default preferences
   */
  static getDefaultPreferences() {
    return {
      emailNotifications: true,
      applicationUpdates: true,
      jobMatches: true,
      messageNotifications: true,
      profileTips: true
    };
  }

  /**
   * Create notification for application status change
   * @param {string} userId - User ID
   * @param {string} applicationId - Application ID
   * @param {string} status - New status
   * @param {Object} jobData - Job data
   * @returns {Object} Result of notification creation
   */
  static async createApplicationStatusNotification(userId, applicationId, status, jobData) {
    const statusMessages = {
      viewed: `Your application for ${jobData?.title || 'a position'} has been viewed by the employer.`,
      accepted: `Congratulations! Your application for ${jobData?.title || 'a position'} has been accepted.`,
      rejected: `Your application for ${jobData?.title || 'a position'} was not selected this time.`
    };

    const statusTitles = {
      viewed: 'Application Viewed',
      accepted: 'Application Accepted',
      rejected: 'Application Update'
    };

    return await this.createNotification({
      userId,
      type: 'application',
      title: statusTitles[status] || 'Application Update',
      message: statusMessages[status] || `Your application status has been updated to ${status}.`,
      relatedId: applicationId,
      metadata: { jobData, status }
    });
  }

  /**
   * Format timestamp to "time ago" format
   * @param {Date} date - Date object
   * @returns {string} Formatted time string
   */
  static formatTimeAgo(date) {
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);
    
    if (diffInSeconds < 60) {
      return 'Just now';
    } else if (diffInSeconds < 3600) {
      const minutes = Math.floor(diffInSeconds / 60);
      return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
    } else if (diffInSeconds < 86400) {
      const hours = Math.floor(diffInSeconds / 3600);
      return `${hours} hour${hours > 1 ? 's' : ''} ago`;
    } else if (diffInSeconds < 172800) {
      return 'Yesterday';
    } else if (diffInSeconds < 604800) {
      const days = Math.floor(diffInSeconds / 86400);
      return `${days} day${days > 1 ? 's' : ''} ago`;
    } else {
      return date.toLocaleDateString();
    }
  }
}

export default NotificationService; 